import React, { memo } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import { Box, Card, CardContent, CardHeader, Chip, Stack, Typography } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PublicIcon from '@mui/icons-material/Public';
import CloudIcon from '@mui/icons-material/Cloud';
import HubIcon from '@mui/icons-material/Hub';

// ─────────────────────────────────────────────────────────────────────────────
// Shared Service Node  (Bibliobus / EventManager shared host)
// ─────────────────────────────────────────────────────────────────────────────
interface SharedServiceNodeData { label: string; }

const SharedServiceNodeInner: React.FC<NodeProps<SharedServiceNodeData>> = ({ data }) => {
    // label from backend: "HOST\n[Bibliobus+EventManager]" or similar
    const parts = (data.label ?? '').split('\n');
    const hostname = parts[0] ?? '';
    const serviceChips = (parts[1] ?? '').replace('[', '').replace(']', '').split('+').filter(Boolean);

    return (
        <>
            <Handle type="target" position={Position.Bottom} />
            <Handle type="source" position={Position.Top} />
            <Card elevation={2} sx={{ minWidth: 140, maxWidth: 190, borderRadius: '6px', border: '1.5px solid #8b5cf6', overflow: 'visible' }}>
                <CardHeader
                    sx={{ pb: 0.3, pt: 0.8, px: 1.2 }}
                    avatar={<HubIcon sx={{ fontSize: 22, color: '#8b5cf6' }} />}
                    title={
                        <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2 }}>
                            {hostname}
                        </Typography>
                    }
                />
                <CardContent sx={{ pt: 0, pb: '6px !important', px: 1.2 }}>
                    <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap sx={{ mb: 0.4 }}>
                        {serviceChips.map((svc, i) => (
                            <Chip key={i} label={svc} size="small"
                                sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#ede9fe', color: '#6b21a8', '& .MuiChip-label': { px: 0.6 } }} />
                        ))}
                    </Stack>
                    <Typography sx={{ fontSize: '0.58rem', opacity: 0.6 }}>Shared service host</Typography>
                </CardContent>
            </Card>
        </>
    );
};
export const SharedServiceNode = memo(SharedServiceNodeInner);

// ─────────────────────────────────────────────────────────────────────────────
// SMTP Node  (email relay server)
// ─────────────────────────────────────────────────────────────────────────────
const SmtpNodeInner: React.FC<NodeProps> = ({ data }) => {
    const hostname = (data.label ?? '').split('\n')[0];

    return (
        <>
            <Handle type="target" position={Position.Bottom} />
            <Handle type="source" position={Position.Top} />
            <Card elevation={2} sx={{ minWidth: 130, maxWidth: 180, borderRadius: '6px', border: '1.5px solid #ec4899', overflow: 'visible' }}>
                <CardHeader
                    sx={{ pb: 0.3, pt: 0.8, px: 1.2 }}
                    avatar={<EmailIcon sx={{ fontSize: 22, color: '#ec4899' }} />}
                    title={
                        <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2 }}>
                            {hostname}
                        </Typography>
                    }
                />
                <CardContent sx={{ pt: 0, pb: '6px !important', px: 1.2 }}>
                    <Chip label="SMTP" size="small"
                        sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#fce7f3', color: '#9d174d', '& .MuiChip-label': { px: 0.6 } }} />
                    <Typography sx={{ fontSize: '0.58rem', opacity: 0.6, mt: 0.4 }}>Email relay server</Typography>
                </CardContent>
            </Card>
        </>
    );
};
export const SmtpNode = memo(SmtpNodeInner);

// ─────────────────────────────────────────────────────────────────────────────
// Proxy Node  (internet gateway)
// ─────────────────────────────────────────────────────────────────────────────
const ProxyNodeInner: React.FC<NodeProps> = ({ data }) => {
    const hostname = (data.label ?? '').split('\n')[0];

    return (
        <>
            <Handle type="target" position={Position.Bottom} />
            <Handle type="source" position={Position.Top} />
            <Card elevation={2} sx={{ minWidth: 130, maxWidth: 180, borderRadius: '6px', border: '1.5px solid #f59e0b', overflow: 'visible' }}>
                <CardHeader
                    sx={{ pb: 0.3, pt: 0.8, px: 1.2 }}
                    avatar={<PublicIcon sx={{ fontSize: 22, color: '#f59e0b' }} />}
                    title={
                        <Typography sx={{ fontWeight: 700, fontSize: '0.7rem', lineHeight: 1.2 }}>
                            {hostname}
                        </Typography>
                    }
                />
                <CardContent sx={{ pt: 0, pb: '6px !important', px: 1.2 }}>
                    <Chip label="Proxy" size="small"
                        sx={{ fontSize: '0.6rem', height: 16, bgcolor: '#fef3c7', color: '#92400e', '& .MuiChip-label': { px: 0.6 } }} />
                    <Typography sx={{ fontSize: '0.58rem', opacity: 0.6, mt: 0.4 }}>Internet gateway</Typography>
                </CardContent>
            </Card>
        </>
    );
};
export const ProxyNode = memo(ProxyNodeInner);

// ─────────────────────────────────────────────────────────────────────────────
// Web Endpoint Node  (cutter-report.lectra.com / datacollect.mylectra.com)
// ─────────────────────────────────────────────────────────────────────────────
const ENDPOINT_META: Record<string, { displayName: string; chips: string[]; color: string; bg: string; textColor: string }> = {
    cutterreport: { displayName: 'cutter-report.lectra.com', chips: ['ZIP Report', 'Maintenance Contract'], color: '#059669', bg: '#d1fae5', textColor: '#065f46' },
    datacollect:  { displayName: 'datacollect.mylectra.com', chips: ['Dashboard', 'Algopex'],               color: '#0891b2', bg: '#cffafe', textColor: '#164e63' },
};

interface EndpointNodeData { label: string; }

const EndpointNodeInner: React.FC<NodeProps<EndpointNodeData>> = ({ id, data }) => {
    // id pattern: "endpoint:cutterreport" or "endpoint:datacollect"
    const key = (id ?? '').split(':')[1] ?? '';
    const meta = ENDPOINT_META[key] ?? {
        displayName: data.label ?? id,
        chips: ['Web server'],
        color: '#059669',
        bg: '#d1fae5',
        textColor: '#065f46'
    };

    return (
        <>
            {/* Endpoints are top-level — only a target handle (nothing above them) */}
            <Handle type="target" position={Position.Bottom} />
            <Card elevation={3} sx={{ minWidth: 150, maxWidth: 210, borderRadius: '6px', border: `2px solid ${meta.color}`, bgcolor: meta.bg, overflow: 'visible' }}>
                <CardHeader
                    sx={{ pb: 0.3, pt: 0.8, px: 1.2 }}
                    avatar={<CloudIcon sx={{ fontSize: 22, color: meta.color }} />}
                    title={
                        <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', lineHeight: 1.2, color: meta.textColor }}>
                            {meta.displayName}
                        </Typography>
                    }
                />
                <CardContent sx={{ pt: 0, pb: '6px !important', px: 1.2 }}>
                    <Stack direction="row" spacing={0.4} flexWrap="wrap" useFlexGap>
                        {meta.chips.map((chip, i) => (
                            <Chip key={i} label={chip} size="small"
                                sx={{ fontSize: '0.6rem', height: 16, bgcolor: meta.color, color: '#fff', '& .MuiChip-label': { px: 0.6 } }} />
                        ))}
                    </Stack>
                </CardContent>
            </Card>
        </>
    );
};
export const EndpointNode = memo(EndpointNodeInner);

// ─────────────────────────────────────────────────────────────────────────────
// Product Group Node  (background rounded rectangle grouping cutters by product)
// ─────────────────────────────────────────────────────────────────────────────
const ProductGroupNodeInner: React.FC<NodeProps> = ({ data }) => (
    <Box sx={{ display: 'inline-flex', px: 1, pt: 0.5, pointerEvents: 'none', userSelect: 'none' }}>
        <Typography
            sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                color: '#fff',
                bgcolor: 'rgba(37, 99, 235, 0.75)',
                px: 0.8,
                py: 0.15,
                borderRadius: '4px',
                letterSpacing: '0.04em',
                lineHeight: 1.5,
            }}
        >
            {data.label as string}
        </Typography>
    </Box>
);
export const ProductGroupNode = memo(ProductGroupNodeInner);
